// frontend/src/components/shop/MythrilPurchaseModal.js
// Shop V1 — Mythril purchase modal.
// Stipend tier wired to POST /api/v2/shop/daily-reward/claim/ (real endpoint, PR6 backend).
// Fiat tiers are visible but disabled (Coming Soon) — real IAP is post-launch.

import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Modal, Button } from '../ui';
import { MYTHRIL_TIERS } from '../../data/shopData';
import AuthContext from '../../auth/AuthContext';
import { claimDailyStipend } from '../../api/shopApi';
import './MythrilPurchaseModal.css';

// Early Access stipend tier definition — 100 Mythril, free, no fiat
const EARLY_ACCESS_STIPEND = {
    id: 'early-access-stipend',
    mythril: 100,
    label: 'Early Access Daily Mythril Stipend',
    description: 'Claim your free daily Mythril during Early Access',
};

/**
 * MythrilPurchaseModal
 *
 * Displays the Early Access stipend tier (active/free, once per calendar day per server cycle)
 * and 4 IAP tiers (Coming Soon).
 * Stipend claim calls POST /api/v2/shop/daily-reward/claim/ and updates AuthContext balance.
 * Both granted_now:true and replayed:true update the balance from the server response.
 *
 * @param {object}   props
 * @param {boolean}  props.isOpen           — controls modal visibility
 * @param {Function} props.onClose          — called when modal is dismissed
 * @param {Function} props.onPurchase       — called with mythrilAmount on mock purchase (fiat tiers, future)
 */
function MythrilPurchaseModal({ isOpen, onClose, onPurchase }) {
    const { setMythrilBalance } = useContext(AuthContext);

    const [loadingTierId, setLoadingTierId] = useState(null);
    const [stipendLoading, setStipendLoading] = useState(false);
    const [stipendMessage, setStipendMessage] = useState('');

    // Reset message when modal opens/closes so stale feedback doesn't persist
    useEffect(() => {
        if (!isOpen) {
            setStipendMessage('');
        }
    }, [isOpen]);

    // Handles the Early Access stipend claim — calls POST /api/v2/shop/daily-reward/claim/
    const handleStipendClaim = useCallback(async () => {
        if (stipendLoading) return; // prevent double-click
        setStipendLoading(true);
        setStipendMessage('');
        try {
            const result = await claimDailyStipend();
            // Always update balance from server response — covers both granted_now and replayed
            if (typeof result.balance === 'number') {
                setMythrilBalance(result.balance);
            }
            if (result.granted_now) {
                // First claim this cycle — show success
                setStipendMessage(`✅ ${result.amount} Mythril claimed! Balance updated.`);
            } else {
                // Replay — already claimed today
                setStipendMessage('Already claimed today. Come back tomorrow!');
            }
        } catch (err) {
            // Graceful error — never crash
            const msg = err.message || 'Failed to claim daily reward.';
            setStipendMessage(`❌ ${msg}`);
        } finally {
            setStipendLoading(false);
        }
    }, [stipendLoading, setMythrilBalance]);

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Buy Mythril"
            size="md"
            closeOnOverlayClick={true}
        >
            <div className="mythril-modal-body">
                {/* diamond SVG icon in subtitle */}
                <p className="mythril-modal-subtitle">
                    <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon mythril-modal-title-icon" />
                    {' '}Mythril powers your pulls. Choose a bundle below.
                </p>

                {/* ----------------------------------------------------------------
                    Early Access Daily Stipend — active, free, at top of list
                    Wired to POST /api/v2/shop/daily-reward/claim/ (real endpoint)
                    Server is authoritative on claim eligibility and balance.
                    ---------------------------------------------------------------- */}
                <div className="mythril-tier-card mythril-tier-card--stipend">
                    <div className="mythril-tier-info">
                        <span className="mythril-stipend-label">
                            {EARLY_ACCESS_STIPEND.label}
                        </span>
                        <span className="mythril-tier-amount">
                            <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon mythril-tier-icon" />
                            {' '}{EARLY_ACCESS_STIPEND.mythril.toLocaleString()} Mythril
                        </span>
                        <p className="mythril-stipend-description">
                            {EARLY_ACCESS_STIPEND.description}
                        </p>
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleStipendClaim}
                        disabled={stipendLoading}
                        className="mythril-tier-button mythril-tier-button--free"
                    >
                        {stipendLoading ? 'Claiming…' : 'Claim 100 Mythril'}
                    </Button>
                    {stipendMessage && (
                        <p className="mythril-stipend-message">{stipendMessage}</p>
                    )}
                </div>

                {/* ----------------------------------------------------------------
                    Fiat IAP tiers — Coming Soon (visible but disabled)
                    MOCK — pending real IAP backend
                    ---------------------------------------------------------------- */}
                <div className="mythril-tier-list">
                    {MYTHRIL_TIERS.map((tier) => (
                        <div key={tier.id} className="mythril-tier-card mythril-tier-card--coming-soon">
                            <div className="mythril-tier-info">
                                <span className="mythril-tier-amount">
                                    <img src="/art/diamond_400_white_bg.svg" alt="Mythril" className="mythril-icon mythril-tier-icon" />
                                    {' '}{tier.mythril.toLocaleString()} Mythril
                                </span>
                                {tier.bonus && (
                                    <span className="mythril-tier-bonus">{tier.bonus}</span>
                                )}
                            </div>
                            {/* Fiat tiers are disabled — real IAP is post-launch */}
                            <Button
                                variant="primary"
                                onClick={() => {}}
                                disabled={true}
                                className="mythril-tier-button"
                            >
                                Coming Soon
                            </Button>
                        </div>
                    ))}
                </div>

                <p className="mythril-modal-legal">
                    Real-money purchasing requires additional legal review before activation.
                    See design doc §7 for compliance requirements.
                </p>
            </div>
        </Modal>
    );
}

export default MythrilPurchaseModal;
